import { zarazService } from '@/services';
import { AppState } from '@/store';
import { ZarazEcommerce, ZarazEcommerceProduct } from '@c2s-npm/zaraz-utils';
import { CartItem } from '@types';
import axios from 'axios';
import Big from 'big.js';
import {
  CartActionTypes,
  CartEstimatedAmountsActionTypes,
  CartItemAddActionTypes,
  CartItemRemoveActionTypes,
  CartItemUpdateActionTypes,
} from '../constants/cart.constants';
import { CartItemListActionTypes } from '../features/cart/redux/constants';
import { processPromoCodeFromTotals } from './promoCode.actions';
import queryClient from '@/query-client';

const errorHandlerFactory = (failAction, defaultMessage) => (error) => (dispatch) => {
  const err = error.message || error.response.message || defaultMessage;

  dispatch({
    type: failAction,
    payload: err,
  });
  dispatch({
    type: CartActionTypes.SHOW_ALERT,
    payload: {
      message: err,
      type: 'danger',
      duration: 3500,
    },
  });
};

export const setCartItems = (data) => async (dispatch) => {
  const structuredData = {
    ...data,
    items: structureCartItems(data.items),
  };

  const cartItemsCountSelector = document.querySelector('#cartItemsCount span');
  if (cartItemsCountSelector) cartItemsCountSelector.innerHTML = structuredData.summary.itemCount;

  if (structuredData.removedOutOfStockItems > 0) {
    dispatch({
      type: CartActionTypes.SHOW_ALERT,
      payload: {
        message: 'One or more items in your cart are no longer in stock and have been removed.',
        type: 'danger',
        duration: false,
      },
    });
  }

  dispatch({
    type: CartItemListActionTypes.SUCCESS,
    payload: structuredData,
  });

  trackCartUpdated(structuredData.items);
};

const trackCartUpdated = (items: any[]) => {
  const payload: ZarazEcommerce = {
    products: items.map<ZarazEcommerceProduct>((i) => ({
      product_id: i.id.toString(),
      sku: i.upc,
      category: i.categoryName,
      name: i.displayName,
      brand: 'RTIC Outdoors',
      price: i.rate,
      currency: 'usd',
      quantity: i.quantity,

      url: new URL(i.url, window.location.origin).toString(),
      imageUrl: i.webImages.default.zoomed.url,
      comparePrice: i.baseRate,
    })),
    currency: 'usd',
    total: items.reduce((total: Big, i: any) => total.plus(i.rateQuantity), Big(0)).toNumber(),
  };

  zarazService.track('Cart Updated', payload);
};

const cartOp = (op, quantity, item, additionalData = {}) => ({
  op,
  quantity,
  lineId: item.lineId,
  id: item.id,
  memberItemIds: item.memberItems?.map((i) => i.id),
  memberItems: item.memberItems,
  designId: item.customInfo?.designId,
  graphicType: item.customInfo?.graphicType,
  printType: item.customInfo?.printType,
  promoCodeId: item.promoCodeId,
  ...additionalData,
  item,
});

const standardizeOp = (op) => {
  if (Array.isArray(op)) {
    return { items: op };
  } else if (op.items) {
    return op;
  } else {
    return { items: [op] };
  }
};

const checkForCartActionConfirmation = (rawOp, next) => (dispatch, getState: () => AppState) => {
  const op = standardizeOp(rawOp);

  const items = getState().cartItemList.items;
  const hasFreeGift = !!items.find((i) => i.isFreeGift);
  if (hasFreeGift) {
    axios.post(jsRoutes.controllers.Cart.noop().url, op).then(({ data }) => {
      const { potentialFreeGift } = data;
      const invalidFreeGifts = data.items
        .filter((i) => i.isInvalidFreeGift)
        .map((g) => {
          const promo = potentialFreeGift.find((p) => p.promoCodeId === g.promoCodeId);
          return Object.assign({}, g, { promo });
        });
      const hasInvalid = invalidFreeGifts.length > 0;
      if (hasInvalid) {
        dispatch({ type: CartItemRemoveActionTypes.STOP });
        dispatch({ type: CartItemUpdateActionTypes.STOP });
        const opWithInvalid = {
          ...op,
          items: op.items.concat(invalidFreeGifts.map((item) => cartOp('remove', 0, item))),
        };
        dispatch({
          type: CartActionTypes.SHOW_INVALID_GIFT_ALERT,
          op,
          invalidFreeGifts,
          next: () => {
            return next(opWithInvalid);
          },
        });
      } else {
        dispatch(next(op));
      }
    });
  } else {
    dispatch(next(op));
  }
};

export const cancelPendingCartAction = () => {
  return {
    type: CartActionTypes.HIDE_INVALID_GIFT_ALERT,
  };
};

export const continuePendingCartAction = () => (dispatch, getState) => {
  dispatch(cancelPendingCartAction());
  dispatch(getState().cartPendingAction.next());
};

export const updateCartItem =
  (item, quantity, additionalData = {}) =>
  async (dispatch) => {
    const errorHandler = errorHandlerFactory(
      CartItemUpdateActionTypes.FAIL,
      'Failed updating cart item quantity'
    );
    try {
      dispatch({ type: CartActionTypes.HIDE_ALERT });
      dispatch({ type: CartItemUpdateActionTypes.REQUEST });
      dispatch(
        checkForCartActionConfirmation(cartOp('edit', quantity, item, additionalData), updateCartItemCallback)
      );
    } catch (error) {
      dispatch(errorHandler(error));
    }
  };

export const multiUpdateCartItems = (items) => async (dispatch) => {
  const errorHandler = errorHandlerFactory(
    CartItemUpdateActionTypes.FAIL,
    'Failed updating cart item quantity'
  );
  try {
    dispatch({ type: CartActionTypes.HIDE_ALERT });
    dispatch({ type: CartItemUpdateActionTypes.REQUEST });
    const op = {
      items: items.map(({ operation, item }) => ({
        ...operation,
        item,
      })),
    };
    dispatch(checkForCartActionConfirmation(op, updateCartItemCallback));
  } catch (error) {
    dispatch(errorHandler(error));
  }
};

const updateCartItemCallback = (op) => async (dispatch) => {
  const errorHandler = errorHandlerFactory(
    CartItemUpdateActionTypes.FAIL,
    'Failed updating cart item quantity'
  );
  try {
    dispatch({ type: CartItemUpdateActionTypes.REQUEST });

    axios
      .post(jsRoutes.controllers.Cart.update().url, op)
      .then(({ data }) => {
        dispatch(setCartItems(data));

        dispatch({
          type: CartItemUpdateActionTypes.SUCCESS,
          payload: data,
        });
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });

        dispatch({
          type: CartActionTypes.SHOW_ALERT,
          payload: {
            message: 'Quantity has been updated successfully',
            type: 'success',
            duration: 3500,
          },
        });

        dispatch(getCartEstimatedAmounts());

        trackCartChange(
          op.items.filter((i) => !!i.item).map((i) => ({ ...i.item, newQuantity: i.quantity }))
        );
      })
      .catch((error) => dispatch(errorHandler(error)));
  } catch (error) {
    dispatch(errorHandler(error));
  }
};

export const removeCartItem =
  (item, additionalData = {}) =>
  async (dispatch) => {
    const errorHandler = errorHandlerFactory(CartItemRemoveActionTypes.FAIL, 'Failed removing cart item');
    try {
      dispatch({ type: CartActionTypes.HIDE_ALERT });
      dispatch({ type: CartItemRemoveActionTypes.REQUEST });
      dispatch(
        checkForCartActionConfirmation(cartOp('remove', 0, item, additionalData), removeCartItemCallback)
      );
    } catch (error) {
      dispatch(errorHandler(error));
    }
  };

const removeCartItemCallback = (op) => async (dispatch) => {
  const errorHandler = errorHandlerFactory(CartItemRemoveActionTypes.FAIL, 'Failed removing cart item');
  try {
    dispatch({ type: CartItemRemoveActionTypes.REQUEST });
    axios
      .post(jsRoutes.controllers.Cart.update().url, op)
      .then(({ data }) => {
        dispatch(setCartItems(data));

        dispatch({
          type: CartItemRemoveActionTypes.SUCCESS,
          payload: data,
        });

        dispatch({
          type: CartActionTypes.SHOW_ALERT,
          payload: {
            message: 'Item has been removed successfully',
            type: 'success',
            duration: 3500,
          },
        });

        dispatch(getCartEstimatedAmounts());

        trackCartChange(
          op.items.filter((i) => !!i.item).map((i) => ({ ...i.item, newQuantity: i.quantity }))
        );
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      })
      .catch((error) => dispatch(errorHandler(error)));
  } catch (error) {
    dispatch(errorHandler(error));
  }
};

export const getCartEstimatedAmounts =
  (zip = null) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: CartEstimatedAmountsActionTypes.REQUEST });

      const { data } = await axios.get(jsRoutes.controllers.Cart.totals().url, {
        params: {
          zip,
        },
      });

      // fallback to old response format that is only summary
      const summary = data.summary || data;

      dispatch({
        type: CartEstimatedAmountsActionTypes.SUCCESS,
        payload: summary,
      });

      processPromoCodeFromTotals(summary)(dispatch, getState);
    } catch (error) {
      dispatch({
        type: CartEstimatedAmountsActionTypes.FAIL,
        payload: error.message || error.response.message || 'Failed getting estimated amounts',
      });
    }
  };

const BundleMemberAttrs = ['lineId', 'id', 'designId', 'graphicType', 'printType'];
const ItemAttrs = [
  'lineId',
  'id',
  'quantity',
  'displayName',
  'rateFormatted',
  ['memberItemIds', 'array'],
  ['memberItems', 'array', BundleMemberAttrs],
  'baseShopItemId',
  'designId',
  'graphicType',
  'printType',
  'promoCodeId',
];
const blacklist = ['object', 'undefined'];
const objectWith = (obj, fields) => {
  return fields.reduce((a, f) => {
    if (Array.isArray(f)) {
      const [field, fieldType, fieldAttrs] = f;
      if (fieldType === 'array' && Array.isArray(obj[field])) {
        if (fieldAttrs) {
          a[field] = obj[field].map((c) => objectWith(c, fieldAttrs));
        } else {
          // assume array of scalar values if no attributes supplied
          a[field] = obj[field].filter((c) => !blacklist.includes(typeof c));
        }
      } else {
        console.warn('Unhandled field type', f);
      }
    } else if (!blacklist.includes(typeof obj[f])) {
      // enforce scalar values
      a[f] = obj[f];
    }
    return a;
  }, {});
};

export const addToCart = (item, siteId) => async (dispatch) => {
  try {
    dispatch({ type: CartItemAddActionTypes.REQUEST });

    const cartItems = [item.item];
    const modalItems = [item.item];

    if (item.addOns?.length > 0) {
      modalItems.push(...item.addOns);
      cartItems.push(...item.addOns);
    }

    if (item?.upsell?.length > 0) {
      modalItems.push(...item.upsell);
      cartItems.push(...item.upsell);
    }

    if (item.bundleItems?.length > 0) {
      cartItems[0].memberItemIds = item.bundleItems.map((i) => i.id);
      cartItems[0].memberItems = item.bundleItems;

      modalItems[0].bundleItems = item.bundleItems;
    }

    const cleanedItems = cartItems.map((o) => {
      return objectWith(o, ItemAttrs);
    });

    const response = await axios.post(jsRoutes.controllers.Cart.addMultiModal().url, cleanedItems);

    const cartItemsCountSelector = document.querySelector('#cartItemsCount span');
    if (cartItemsCountSelector) cartItemsCountSelector.innerHTML = response?.data?.totalQuantity;

    trackCartChange([item.item]);

    dispatch({
      type: CartItemAddActionTypes.SUCCESS,
      payload: modalItems,
    });
    queryClient.invalidateQueries({ queryKey: ['cartItems'] });
  } catch (error) {
    dispatch({
      type: CartItemAddActionTypes.FAIL,
      payload: 'Failed adding item to cart',
    });
  }
};

export const showCartAlert =
  (message, type, duration = 3500, switchMessage = false) =>
  (dispatch) => {
    if (!switchMessage) {
      dispatch({
        type: CartActionTypes.HIDE_ALERT,
      });
    }

    dispatch({
      type: CartActionTypes.SHOW_ALERT,
      payload: { message, type, duration },
    });
  };

export const processUpsellItems = () => {};

export const structureCartItems = (items: CartItem[]) => {
  if (!items || items.length === 0) return [];

  const base = items.filter((i) => !i?.upsellInfo?.baseItemId);
  const upsell = items.filter((i) => !!i?.upsellInfo?.baseItemId);

  const structuredItems = base.reduce((a, item) => {
    const baseId = item.isBundle ? item.memberItems[0].id : item.id;
    const upsellItems = upsell
      .filter((u) => baseId === u.upsellInfo.baseItemId)
      .map((u) => {
        const existing = a
          .filter((i) => !!i.upsellItems.find((u) => baseId === u.upsellInfo.baseItemId))
          .reduce((a, i) => a + i.quantity, 0);
        return { ...u, quantity: Math.min(item.quantity, u.quantity - existing) };
      })
      .filter((u) => u.quantity > 0);

    a.push({ ...item, upsellItems });
    return a;
  }, []);

  return structuredItems;
};

type CartItemUpdate = {
  id: number;
  displayName: string;
  categoryName: string;
  rate: number;
  quantity: number;
  newQuantity?: number;
};

const trackCartChange = (items: CartItemUpdate[]) => {
  items.forEach((i) => {
    const changeQuantity =
      i.newQuantity !== undefined ? Big(i.newQuantity).minus(i.quantity).toNumber() : i.quantity;
    const trackFn =
      changeQuantity > 0 ? zarazService.ecommerce.productAdded : zarazService.ecommerce.productRemoved;

    trackFn({
      product_id: i.id.toString(),
      name: i.displayName,
      category: i.categoryName,
      quantity: Math.abs(changeQuantity),
      price: i.rate,
      currency: 'usd',
    });
  });
};
